import {
  TRESPONSE_ORDER_DETAILS_PLANNING_CARD,
  TRESPONSE_ORDER_DETAILS_HEADER,
  TRESPONSE_ORDER_DETAILS_LEARNING_CURVE,
  TRESPONSE_ORDER_DETAILS_ALL_LEARNING_CURVES,
  TRESPONSE_ORDER_DETAILS_VIEW_LEARNING_CURVE,
  TSmvTableProductTypes,
  TProductTypes,
} from '@monorepo/types';
import { ModifiersZTableModel } from '@monorepo/ui';
import { THeaderCells, TTableCellType, TTableRowNode, TTableTreeModel } from '../../table.type';

export type TOrderDetailsUploadTableRow = {
  id: number;
  product_type: string;
  product_code: string;
  smv: number;
};

export type TOrderPlanningOrderInfo = {
  userLoadQty: number;
  isSelected: boolean;
} & TRESPONSE_ORDER_DETAILS_PLANNING_CARD['orders'][number];

const createPlanningTableHeaderRow = (
  headerInfo: TRESPONSE_ORDER_DETAILS_PLANNING_CARD['header_info'],
  hasIsRecalibratedColumn: boolean,
): THeaderCells<TOrderPlanningOrderInfo>[] => {
  const headerRow = headerInfo
    .map<THeaderCells<TOrderPlanningOrderInfo>>((hInfo) => ({
      type: 'TEXT_CELL',
      text: hInfo.display_name,
      align: 'center',
    }))
    .concat(
      {
        type: 'TEXT_CELL',
        text: 'Load Qty.',
        align: 'center',
        liftedInput: {
          input: {
            type: 'INPUT',
            subType: 'number',
            min: 1,
            setValue(value) {
              return value.userLoadQty ?? Number(value.order.max_load_qty);
            },
          },
          metadata: {
            keyName: 'load_qty',
            isRequired: true,
          },
        },
      },
      hasIsRecalibratedColumn
        ? [
            {
              type: 'TEXT_CELL',
              text: '',
              widthInPx: 50,
              align: 'center',
            },
          ]
        : [],
      [
        {
          type: 'TEXT_CELL',
          text: '',
          widthInPx: 50,
          align: 'center',
        },
      ],
    );

  return headerRow;
};

const createOrderDetailsEditHeaderSmvHeaderRow = (
  headerInfo: TRESPONSE_ORDER_DETAILS_HEADER['product_type_info']['header_info'],
): THeaderCells<TSmvTableProductTypes>[] => {
  return headerInfo.map((hInfo): THeaderCells<TSmvTableProductTypes> => {
    const currKey = hInfo.order_field_key_info.key as 'smv' | 'product_type';
    return {
      type: 'TEXT_CELL',
      text: hInfo.order_field_key_info.display_name,
      liftedInput:
        currKey === 'smv' && hInfo.edit_info.editable
          ? {
              input: {
                type: 'INPUT',
                subType: 'number',
                setValue: (v: TSmvTableProductTypes) => v[currKey],
              },
              metadata: {
                keyName: currKey,
                label: hInfo.order_field_key_info.display_name,
                isRequired: true,
              },
            }
          : undefined,
    };
  });
};

const getPlanningTableRows = (
  headerInfo: TRESPONSE_ORDER_DETAILS_PLANNING_CARD['header_info'],
  rowItem: TOrderPlanningOrderInfo,
  hasIsRecalibratedColumn: boolean,
) => {
  const rows = headerInfo
    .map<TTableCellType>((hInfo) => ({
      type: 'TEXT_CELL' as const,
      text: rowItem.order[hInfo.key],
      align: 'center',
    }))
    .concat({
      type: 'TEXT_CELL',
      text: rowItem.userLoadQty ?? rowItem.order.max_load_qty,
      align: 'center',
    })
    .concat(
      hasIsRecalibratedColumn
        ? rowItem.recalibrated
          ? {
              type: 'ACTION_CELL',
              actionType: 'IS_RECALIBRATED',
              align: 'center',
              actionNotAllowed: true,
              asIcon: true,
              showLargeIcon: false,
              hoverText: 'Order already has some produced quantities.',
            }
          : { type: 'TEXT_CELL', text: '' }
        : [],
      rowItem.opl_closed
        ? {
            type: 'ACTION_CELL' as const,
            actionType: 'INFO' as const,
            customIconPath: 'assets/icon/common/close-opl-success.svg',
            actionNotAllowed: true,
            align: 'center' as const,
          }
        : {
            type: 'ACTION_CELL' as const,
            actionType: 'CLOSE_OPL' as const,
            align: 'center' as const,
            customTxt: 'Close OPL',
          },
    );

  return rows;
};

const getOrderDetailsEditHeaderSmvHeaderRow = (
  headerInfo: TRESPONSE_ORDER_DETAILS_HEADER['product_type_info']['header_info'],
  rowItem: TSmvTableProductTypes,
) => {
  const rows = [
    ...headerInfo.map((hInfo) => {
      const currKey = hInfo['order_field_key_info']['key'] as 'product_type' | 'smv';
      return {
        type: 'TEXT_CELL',
        text: rowItem[currKey],
      };
    }),
  ] as TTableCellType[];

  return rows;
};

const getOrderDetailsViewLearningCurveModalHeaderRow = (day_count: number) => {
  const daysHeaderArray = [...Array(day_count).keys()].map((index) => {
    return {
      type: 'TEXT_CELL',
      text: 'Day' + (index + 1),
    };
  });
  return [{ type: 'TEXT_CELL', text: 'Names' }, ...daysHeaderArray] as TTableCellType[];
};

const getOrderDetailsViewLearningCurveModalRows = (
  item: TRESPONSE_ORDER_DETAILS_VIEW_LEARNING_CURVE['learning_curve'],
) => {
  const efficiencies = item.efficiencies.map((dayEff) => {
    return {
      type: 'TEXT_CELL',
      text: dayEff.efficiency,
    };
  });
  const nameCell = { type: 'TEXT_CELL', text: item.name };
  return [nameCell, ...efficiencies] as TTableCellType[];
};

export const ORDER_DETAILS_PLANNING_CARD_TABLE_MODEL = {
  createModel: (
    headerInfo: TRESPONSE_ORDER_DETAILS_PLANNING_CARD['header_info'],
    rowInfo: TOrderPlanningOrderInfo[],
    hasIsRecalibratedColumn: boolean = true,
  ): TTableTreeModel<TOrderPlanningOrderInfo> => {
    return ModifiersZTableModel.createTableModel(
      rowInfo,
      {
        padding: 24,
        select: {
          position: 'START',
          isMulti: true,
          size: 'DEFAULT',
          stickyType: 'LEFT',
        },

        edit: {
          hasAddRow: false,
          isEditOnly: false,
        },

        headerConfig: createPlanningTableHeaderRow(headerInfo, hasIsRecalibratedColumn),
      },
      (item: TOrderPlanningOrderInfo) => {
        return {
          type: 'ROW' as const,
          rowSelectionInfo: {
            isSelected: !!item.isSelected,
            isDisabled: item.status === 'COMPLETELY_PLANNED' || !!item.opl_closed,
            disabledReason:
              item.status === 'COMPLETELY_PLANNED'
                ? 'Order already planned completely'
                : item.opl_closed
                ? 'OPL is closed'
                : undefined,
          },
          rowItem: item,
          cells: getPlanningTableRows(headerInfo, item, hasIsRecalibratedColumn),
        };
      },
    );
  },
};

export const ORDER_DETAILS_EDIT_HEADER_TABLE_MODEL = {
  createSmvModel: (
    headerInfo: TRESPONSE_ORDER_DETAILS_HEADER['product_type_info']['header_info'],
    rows: TSmvTableProductTypes[],
    isEditDisabled: boolean,
  ) => {
    const headerFields = ['smv', 'product_type'];
    headerInfo = headerInfo.filter((hInfo) => headerFields.includes(hInfo.order_field_key_info.key));

    return ModifiersZTableModel.createTableModel(
      rows,
      {
        edit: {
          hasAddRow: false,
          disableEdit: isEditDisabled,
        },
        headerConfig: createOrderDetailsEditHeaderSmvHeaderRow(headerInfo),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getOrderDetailsEditHeaderSmvHeaderRow(headerInfo, item),
        };
      },
    );
  },

  createDefaultCountModel: (rows: TProductTypes[], showIronmanAndHelper: boolean) => {
    return ModifiersZTableModel.createTableModel(
      rows,
      {
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'Product Type',
          },
          {
            type: 'TEXT_CELL',
            text: 'Manpower',
            liftedInput: {
              input: {
                type: 'INPUT',
                subType: 'number',
                setValue: (v: TProductTypes) => v.man_power,
                min: 0,
              },
              metadata: {
                keyName: 'man_power',
                label: 'Manpower',
                isRequired: false,
              },
            },
          },
          ...(showIronmanAndHelper
            ? [
                {
                  type: 'TEXT_CELL' as const,
                  text: 'Helper',
                  liftedInput: {
                    input: {
                      type: 'INPUT' as const,
                      subType: 'number' as const,
                      setValue: (v: TProductTypes) => (v.helper === null ? undefined : v.helper),
                      min: 0,
                    },
                    metadata: {
                      keyName: 'helper',
                      label: 'Helper',
                    },
                  },
                },
                {
                  type: 'TEXT_CELL' as const,
                  text: 'Ironman',
                  liftedInput: {
                    input: {
                      type: 'INPUT' as const,
                      subType: 'number' as const,
                      setValue: (v: TProductTypes) => (v.iron_man === null ? undefined : v.iron_man),
                      min: 0,
                    },
                    metadata: {
                      keyName: 'iron_man',
                      label: 'Ironman',
                    },
                  },
                },
              ]
            : []),
        ],
      },
      (item): TTableRowNode<TProductTypes> => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.product_type,
            },
            {
              type: 'TEXT_CELL',
              text: item.man_power ?? '- -',
            },
            ...(showIronmanAndHelper
              ? [
                  {
                    type: 'TEXT_CELL' as const,
                    text: item.helper ?? '- -',
                  },
                  {
                    type: 'TEXT_CELL' as const,
                    text: item.iron_man ?? '- -',
                  },
                ]
              : []),
          ],
        };
      },
    );
  },
};

export const ORDER_DETAILS_LEARNING_CURVE_TABLE_MODEL = {
  createModel: (
    rows: TRESPONSE_ORDER_DETAILS_LEARNING_CURVE['learning_curve_and_order_efficiencies'],
    learningCurves: TRESPONSE_ORDER_DETAILS_ALL_LEARNING_CURVES['learning_curves'],
  ) => {
    return ModifiersZTableModel.createTableModel(
      rows,
      {
        headerConfig: [
          {
            type: 'TEXT_CELL',
            text: 'FACTORY NAMES',
          },
          {
            type: 'TEXT_CELL',
            text: 'LEARNING CURVE',

            liftedInput: {
              input: {
                type: 'DROPDOWN',
                options: learningCurves.map((d) => ({ value: d.id, displayName: d.name })),
                setValue: (v) => v['learning_curve_info']?.id,
              },
              metadata: {
                keyName: 'learningCurve',
                label: 'Learning Curve',
                isRequired: false,
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: 'ORDER EFFICIENCY',
            liftedInput: {
              input: {
                type: 'INPUT',
                subType: 'number',
                setValue: (v) => v['order_efficiency'],
                min: 0,
              },
              metadata: {
                keyName: 'orderEfficiency',
                label: 'Order Efficiency',
                isRequired: false,
              },
            },
          },
          {
            type: 'TEXT_CELL',
            text: '',
            widthInPx: 32,
          },
        ],
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: [
            {
              type: 'TEXT_CELL',
              text: item.factory_info.name,
            },
            {
              type: 'TEXT_CELL',
              text: item.learning_curve_info?.name || '--',
              hrefActionCB: item.learning_curve_info?.name ? 'LEARNING_CURVE' : undefined,
            },
            {
              type: 'TEXT_CELL',
              text: item.order_efficiency || '--',
            },
            {
              type: 'ACTION_CELL',
              actionType: 'DELETE',
              asIcon: true,
            },
          ],
        };
      },
    );
  },
};

export const ORDER_DETAILS_VIEW_LEARNING_CURVE_MODAL_TABLE_MODEL = {
  createModel: (
    rows: TRESPONSE_ORDER_DETAILS_VIEW_LEARNING_CURVE['learning_curve'][],
    day_count: TRESPONSE_ORDER_DETAILS_VIEW_LEARNING_CURVE['day_count'],
  ) => {
    return ModifiersZTableModel.createTableModel(
      rows,
      {
        headerConfig: getOrderDetailsViewLearningCurveModalHeaderRow(day_count),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getOrderDetailsViewLearningCurveModalRows(item),
        };
      },
    );
  },
};
